import React, { useState, useEffect } from 'react';
import { Modal } from '../../../UI';
import { statisticsService } from '../../../../apiServices';
import { CategoryTransactionsResponse, StatisticsFilters, TransactionInfo } from '../../../../apiServices/types';
import { formatCurrency, formatDate } from '../../../../utils';
import './CategoryTransactionsModal.css';

interface CategoryTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  filters: StatisticsFilters;
  transactionType?: 'expenses' | 'income';
}

interface GroupedReceipt {
  receiptId: string;
  receiptName: string;
  accountName: string;
  date: string;
  transactions: TransactionInfo[];
  totalAmount: number;
}

const CategoryTransactionsModal: React.FC<CategoryTransactionsModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  filters,
  transactionType
}) => {
  const [transactions, setTransactions] = useState<CategoryTransactionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedReceipts, setExpandedReceipts] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && categoryId) {
      loadTransactions();
      setExpandedReceipts(new Set()); // Reset expanded receipts when modal opens
    }
  }, [isOpen, categoryId, filters.dateFrom, filters.dateTo, filters.accountId, transactionType]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const data = await statisticsService.getCategoryTransactions(categoryId, filters, transactionType);
      setTransactions(data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleReceipt = (receiptId: string) => {
    setExpandedReceipts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(receiptId)) {
        newSet.delete(receiptId);
      } else {
        newSet.add(receiptId);
      }
      return newSet;
    });
  };

  const groupedReceipts = React.useMemo(() => {
    if (!transactions) return [];

    const grouped = transactions.transactions.reduce((acc, transaction) => {
      if (!acc[transaction.receiptId]) {
        acc[transaction.receiptId] = {
          receiptId: transaction.receiptId,
          receiptName: transaction.receiptName,
          accountName: transaction.accountName,
          date: transaction.date,
          transactions: [],
          totalAmount: 0
        };
      }
      acc[transaction.receiptId].transactions.push(transaction);
      acc[transaction.receiptId].totalAmount += transaction.amount;
      return acc;
    }, {} as Record<string, GroupedReceipt>);

    return Object.values(grouped)
      .map(receipt => ({
        ...receipt,
        transactions: receipt.transactions.sort((a, b) => b.amount - a.amount)
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }, [transactions]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Позиції: ${categoryName}`}
      size="md"
    >
      {loading ? (
        <div className="transactions-loading">
          <div className="spinner"></div>
          <p>Завантаження позицій...</p>
        </div>
      ) : transactions && transactions.transactions.length > 0 ? (
        <div className="transactions-content">
          <div className="transactions-summary">
            <div className="summary-item">
              <span className="summary-label">Всього позицій:</span>
              <span className="summary-value">{transactions.totalCount}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Загальна сума:</span>
              <span className={`summary-value ${transactionType === 'income' ? 'income' : 'expenses'}`}>
                {formatCurrency(transactions.totalAmount)} ₴
              </span>
            </div>
          </div>

          <div className="receipts-list">
            {groupedReceipts.map((receipt) => {
              const isExpanded = expandedReceipts.has(receipt.receiptId);
              return (
                <div key={receipt.receiptId} className="receipt-group">
                  <div 
                    className="receipt-header"
                    onClick={() => toggleReceipt(receipt.receiptId)}
                  >
                    <div className="stats-receipt-info">
                      <div className="stats-receipt-name">{receipt.receiptName}</div>
                      <div className="stats-receipt-meta">
                        <span className="receipt-date">{formatDate(receipt.date)}</span>
                        <span className="receipt-account">{receipt.accountName}</span>
                      </div>
                    </div>
                    <div className="receipt-actions">
                      <div className={`receipt-total ${transactionType === 'income' ? 'income' : 'expenses'}`}>
                        {formatCurrency(receipt.totalAmount)} ₴
                      </div>
                      <div className={`receipt-arrow ${isExpanded ? 'expanded' : ''}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="receipt-transactions">
                      {receipt.transactions.map((transaction) => (
                        <div key={transaction.positionId} className="transaction-item">
                          <div className="transaction-content">
                            <div className="transaction-name">{transaction.positionName}</div>
                            <div className={`transaction-amount ${transactionType === 'income' ? 'income' : 'expenses'}`}>
                              {formatCurrency(transaction.amount)} ₴
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="transactions-empty">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
          </div>
          <div className="empty-title">Немає позицій</div>
          <div className="empty-text">Для цієї категорії немає позицій у вибраному періоді</div>
        </div>
      )}
    </Modal>
  );
};

export default CategoryTransactionsModal;

